import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { SubscriptionsIndexData } from '@entities/subscription'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useSubscriptionsStore } from '../model/store'

interface SubscriptionsApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/subscriptions` — the index. Returns `{ subscriptions, agents, discounts, meta }`.
 * The `from` / `to` next-invoice range comes straight from the date-range filter as datetime strings.
 */
export const useSubscriptionsApi = async (options?: SubscriptionsApiOptions): Promise<SubscriptionsIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useSubscriptionsStore()

  const url = useAdminRoutes('admin.subscriptions.data', pick({
    q: store.getFilterSearch,
    status: store.getFilterStatuses.length ? store.getFilterStatuses : undefined,
    from: store.getFilterFrom || undefined,
    to: store.getFilterTo || undefined,
    customer_id: store.getFilterCustomerId || undefined,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: SubscriptionsIndexData | null = data.data || null

    if (indexData) {
      store.setSubscriptions(indexData.subscriptions)
      store.setAgents(indexData.agents)
      store.setDiscounts(indexData.discounts)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
